import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useDatabase } from './useDatabase';
import { isSupabaseConfigured, safeSupabaseRequest } from '../lib/supabaseClient';

export interface FuturesOrderParams {
  symbol: string;
  side: 'long' | 'short';
  amount: number;
  leverage: number;
  marginType: 'isolated' | 'cross';
  orderType: 'market' | 'limit';
  price?: number;
  stopLoss?: number;
  takeProfit?: number;
}

export interface FuturesPosition {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  amount: number;
  entryPrice: number;
  currentPrice: number;
  leverage: number;
  marginType: 'isolated' | 'cross';
  margin: number;
  liquidationPrice: number;
  unrealizedPnl: number;
  roi: number;
  stopLoss?: number;
  takeProfit?: number;
  createdAt: string;
}

export interface FuturesOrder {
  id: string;
  symbol: string;
  type: 'limit' | 'market' | 'stop';
  side: 'buy' | 'sell';
  price: number | null;
  amount: number;
  leverage: number;
  marginType: 'isolated' | 'cross';
  status: 'open' | 'filled' | 'cancelled';
  createdAt: string;
  filledAt: string | null;
  positionId: string | null;
  stopLoss?: number | null;
  takeProfit?: number | null;
}

export interface PositionHistoryEntry {
  id: string;
  symbol: string;
  side: 'long' | 'short';
  entryPrice: number;
  exitPrice: number;
  amount: number;
  leverage: number;
  margin: number;
  pnl: number;
  roi: number;
  openTime: string;
  closeTime: string;
  durationSeconds: number;
}

export const useFuturesTrading = () => {
  const { balances, updateBalances } = useDatabase();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [positionHistory, setPositionHistory] = useState<PositionHistoryEntry[]>([]);
  const [activePositions, setActivePositions] = useState<FuturesPosition[]>([]);
  const [openOrders, setOpenOrders] = useState<FuturesOrder[]>([]);

  // Calculate liquidation price
  const calculateLiquidationPrice = (
    side: 'long' | 'short',
    entryPrice: number,
    leverage: number,
    marginType: 'isolated' | 'cross' = 'isolated',
    amount: number = 0,
    totalBalance: number = 0
  ): number => {
    // For isolated margin, use the standard formula
    if (marginType === 'isolated') {
      const maintenanceMarginRatio = 0.05; // 5% maintenance margin
      const liquidationThreshold = (1 / leverage) * (1 - maintenanceMarginRatio);
      
      if (side === 'long') {
        return entryPrice * (1 - liquidationThreshold);
      } else {
        return entryPrice * (1 + liquidationThreshold);
      }
    } 
    // For cross margin, calculate based on total account balance
    else {
      // If amount is too small or balance is too large, use a more reasonable calculation
      if (amount <= 0.0001 || totalBalance <= 0 || amount * entryPrice * leverage < totalBalance * 0.01) {
        // Fall back to a more conservative liquidation price (similar to isolated but with more buffer)
        const maintenanceMarginRatio = 0.05;
        const liquidationThreshold = (1 / leverage) * (1 - maintenanceMarginRatio) * 0.9; // 90% of isolated threshold
        
        if (side === 'long') {
          return entryPrice * (1 - liquidationThreshold);
        } else {
          return entryPrice * (1 + liquidationThreshold);
        }
      }
      
      // For cross margin, liquidation happens when total account equity reaches zero
      let newLiquidationPrice;
      if (side === 'long') {
        // For long positions, price needs to drop enough to wipe out the entire account balance
        // Formula: liquidationPrice = entryPrice - (totalBalance / (amount * leverage))
        const priceDropNeeded = (totalBalance / (amount * leverage));
        newLiquidationPrice = entryPrice - priceDropNeeded;
        
        // Ensure liquidation price is not less than a small positive value
        // and not unrealistically far from entry price
        const minLiqPrice = Math.max(0.01, entryPrice * 0.1); // At least 10% of entry price
        return Math.max(minLiqPrice, newLiquidationPrice);
      } else {
        // For short positions, price needs to rise enough to wipe out the entire account balance
        // Formula: liquidationPrice = entryPrice + (totalBalance / (amount * leverage))
        const priceRiseNeeded = (totalBalance / (amount * leverage));
        newLiquidationPrice = entryPrice + priceRiseNeeded;
        
        // Cap extremely high liquidation prices to a reasonable multiple of entry price
        const maxLiqPrice = entryPrice * 10; // At most 10x entry price
        return Math.min(newLiquidationPrice, maxLiqPrice);
      }
    }
  };

  // Fetch active positions
  const fetchActivePositions = useCallback(async (): Promise<FuturesPosition[]> => {
    setLoading(true);
    setError(null);
    
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty positions');
      setActivePositions([]);
      setLoading(false);
      return [];
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn('User not authenticated, returning empty positions');
        setActivePositions([]);
        setLoading(false);
        return [];
      }
      
      const { data, error: orderError } = await safeSupabaseRequest(
        () => supabase
          .from('futures_positions')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_open', true)
          .order('created_at', { ascending: false }),
        []
      );
        
      if (orderError) {
        console.warn('Error fetching active positions:', orderError);
        setActivePositions([]);
        setLoading(false);
        return [];
      }
      
      const formattedPositions: FuturesPosition[] = data.map(pos => ({
        id: pos.id,
        symbol: pos.symbol,
        side: pos.side,
        entryPrice: parseFloat(pos.entry_price) || 0,
        currentPrice: parseFloat(pos.current_price) || 0,
        amount: parseFloat(pos.amount) || 0,
        leverage: pos.leverage || 1,
        marginType: pos.margin_type,
        liquidationPrice: parseFloat(pos.liquidation_price) || 0,
        unrealizedPnl: parseFloat(pos.unrealized_pnl) || 0,
        margin: parseFloat(pos.margin) || 0,
        roi: parseFloat(pos.roi) || 0,
        stopLoss: pos.sl_price ? parseFloat(pos.sl_price) : undefined,
        takeProfit: pos.tp_price ? parseFloat(pos.tp_price) : undefined,
        createdAt: pos.created_at
      }));
      
      setActivePositions(formattedPositions);
      return formattedPositions;
    } catch (err: any) {
      console.error('Error fetching active positions:', err);
      setError(err.message || 'Failed to fetch active positions');
      setActivePositions([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch open orders
  const fetchOpenOrders = useCallback(async (): Promise<FuturesOrder[]> => {
    setLoading(true);
    setError(null);
    
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, returning empty orders');
      setOpenOrders([]);
      setLoading(false);
      return [];
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        console.warn('User not authenticated, returning empty orders');
        setOpenOrders([]);
        setLoading(false);
        return [];
      }
      
      const { data, error: ordersError } = await safeSupabaseRequest(
        () => supabase
          .from('futures_orders')
          .select('*')
          .eq('user_id', user.id)
          .eq('status', 'open')
          .order('created_at', { ascending: false }),
        []
      );
        
      if (ordersError) {
        console.warn('Error fetching open orders:', ordersError);
        setOpenOrders([]);
        setLoading(false);
        return [];
      }
      
      const formattedOrders: FuturesOrder[] = data.map(order => ({
        id: order.id,
        symbol: order.symbol,
        type: order.type,
        side: order.side,
        price: order.price,
        amount: parseFloat(order.amount),
        leverage: order.leverage,
        marginType: order.margin_type,
        status: order.status,
        createdAt: order.created_at,
        filledAt: order.filled_at,
        positionId: order.position_id,
        stopLoss: order.sl_price,
        takeProfit: order.tp_price
      }));
      
      setOpenOrders(formattedOrders);
      return formattedOrders;
    } catch (err: any) {
      console.error('Error fetching open orders:', err);
      setError(err.message || 'Failed to fetch open orders');
      setOpenOrders([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Open a futures position
  const openPosition = useCallback(async (params: FuturesOrderParams): Promise<string | null> => {
    setLoading(true);
    setError(null);
    
    try {
      const { symbol, side, amount, leverage, marginType, orderType, price, stopLoss, takeProfit } = params;
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      // For limit orders, create a futures_orders entry instead of immediately opening a position
      if (orderType === 'limit' && price) {
        // Calculate required margin
        const notionalValue = amount * price;
        const requiredMargin = notionalValue / leverage;
        
        // Check if user has enough balance
        if (requiredMargin > balances.usdt_balance) {
          throw new Error('Insufficient margin balance');
        }
        
        // Reserve the margin by deducting it from user's balance
        await updateBalances({
          usdt_balance: balances.usdt_balance - requiredMargin
        });
        
        // Create a limit order with user_id
        const { data: order, error: orderError } = await supabase
          .from('futures_orders')
          .insert([{
            user_id: user.id,
            symbol,
            type: orderType,
            side: side === 'long' ? 'buy' : 'sell',
            price,
            amount,
            leverage,
            margin_type: marginType,
            status: 'open',
            tp_price: takeProfit || null,
            sl_price: stopLoss || null
          }])
          .select()
          .single();
          
        if (orderError) {
          // Revert the balance change if order creation fails
          await updateBalances({
            usdt_balance: balances.usdt_balance
          });
          throw orderError;
        }
        
        // Refresh open orders
        await fetchOpenOrders();
        
        return order?.id || null;
      } 
      else {
        // For market orders, use the provided price directly (which should be from WebSocket)
        // Use the provided price (from WebSocket) or throw an error if not available
        let entryPrice = price;
        if (!entryPrice) {
          throw new Error(`No price provided for market order for ${symbol}`);
        }
        
        // Check for recent similar positions to prevent duplicates
        const tenSecondsAgo = new Date();
        tenSecondsAgo.setSeconds(tenSecondsAgo.getSeconds() - 10);
        
        const { data: recentPositions, error: recentError } = await supabase
          .from('futures_positions')
          .select('id, created_at')
          .eq('user_id', user.id)
          .eq('symbol', symbol)
          .eq('side', side)
          .eq('is_open', true)
          .gt('created_at', tenSecondsAgo.toISOString())
          .order('created_at', { ascending: false });
          
        if (recentError) {
          console.warn('Error checking for recent positions:', recentError);
          // Continue anyway, as this is just a duplicate prevention check
        }
        
        // If a recent similar position exists, return its ID instead of creating a new one
        if (recentPositions && recentPositions.length > 0) {
          console.log('Found recent similar position, preventing duplicate:', recentPositions[0]);
          
          // Refresh active positions to ensure UI is up to date
          await fetchActivePositions();
          
          return recentPositions[0].id;
        }
        
        // Calculate position details
        const positionSize = amount * entryPrice;
        const requiredMargin = positionSize / leverage;
        
        // Check if user has enough balance
        if (requiredMargin > balances.usdt_balance) {
          throw new Error('Insufficient margin balance');
        }
        
        // Calculate liquidation price based on margin type
        const liquidationPrice = calculateLiquidationPrice(
          side, 
          entryPrice, 
          leverage, 
          marginType,
          amount,
          balances.usdt_balance
        );
        
        // Deduct margin from user balance
        await updateBalances({
          usdt_balance: balances.usdt_balance - requiredMargin
        });
        
        // Create the position with user_id
        const { data: position, error: positionError } = await supabase
          .from('futures_positions')
          .insert([{
            user_id: user.id,
            symbol,
            side,
            entry_price: entryPrice,
            current_price: entryPrice,
            amount,
            leverage,
            margin_type: marginType,
            liquidation_price: liquidationPrice,
            unrealized_pnl: 0,
            margin: requiredMargin,
            roi: 0,
            is_open: true,
            position_size: positionSize,
            tp_price: takeProfit || null,
            sl_price: stopLoss || null
          }])
          .select()
          .single();
          
        if (positionError) {
          // Revert the balance change if position creation fails
          await updateBalances({
            usdt_balance: balances.usdt_balance + requiredMargin
          });
          throw positionError;
        }
        
        // Refresh active positions
        await fetchActivePositions();
        
        return position?.id || null;
      }
    } catch (err: any) {
      console.error('Error opening futures position:', err);
      setError(err.message || 'Failed to open position');
      return null;
    } finally {
      setLoading(false);
    }
  }, [balances, updateBalances, calculateLiquidationPrice, fetchActivePositions, fetchOpenOrders]);

  // Close a futures position
  const closePosition = useCallback(async (positionId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      // Get current price
      const { data: position } = await supabase
        .from('futures_positions')
        .select('symbol')
        .eq('id', positionId)
        .single();
        
      if (!position) {
        throw new Error('Position not found');
      }
      
      const { data: marketData } = await supabase
        .from('market_data')
        .select('price')
        .eq('symbol', position.symbol)
        .order('timestamp', { ascending: false })
        .limit(1)
        .single();
        
      if (!marketData) {
        throw new Error(`No market data available for ${position.symbol}`);
      }
      
      const exitPrice = parseFloat(marketData.price);
      
      // Call the close_futures_position function
      const { error: rpcError } = await supabase.rpc('close_futures_position', {
        position_id: positionId,
        exit_price: exitPrice
      });
        
      if (rpcError) {
        throw rpcError;
      }
      
      // Refresh active positions
      await fetchActivePositions();
      
      return true;
    } catch (err: any) {
      console.error('Error closing futures position:', err);
      setError(err.message || 'Failed to close position');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchActivePositions]);

  // Update a position's entry price (for manual adjustments)
  const updateEntryPrice = useCallback(async (positionId: string, newEntryPrice: number): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const { error: rpcError } = await supabase.rpc('update_position_entry_price', {
        position_id: positionId,
        new_entry_price: newEntryPrice
      });
        
      if (rpcError) {
        throw rpcError;
      }
      
      // Refresh active positions
      await fetchActivePositions();
      
      return true;
    } catch (err: any) {
      console.error('Error updating entry price:', err);
      setError(err.message || 'Failed to update entry price');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchActivePositions]);

  // Update stop loss or take profit
  const updateStopLossTakeProfit = useCallback(async (
    positionId: string, 
    updates: { stopLoss?: number; takeProfit?: number }
  ): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      const updateData: any = {};
      
      if (updates.stopLoss !== undefined) {
        updateData.sl_price = updates.stopLoss;
      }
      
      if (updates.takeProfit !== undefined) {
        updateData.tp_price = updates.takeProfit;
      }
      
      const { error: updateError } = await supabase
        .from('futures_positions')
        .update(updateData)
        .eq('id', positionId);
        
      if (updateError) {
        throw updateError;
      }
      
      // Refresh active positions
      await fetchActivePositions();
      
      return true;
    } catch (err: any) {
      console.error('Error updating SL/TP:', err);
      setError(err.message || 'Failed to update stop loss/take profit');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchActivePositions]);

  // Load position history
  const loadPositionHistory = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: fetchError } = await supabase
        .from('futures_position_history')
        .select('*')
        .order('close_time', { ascending: false });
        
      if (fetchError) {
        throw fetchError;
      }
      
      if (data) {
        setPositionHistory(data.map(item => ({
          id: item.id,
          symbol: item.symbol,
          side: item.side,
          entryPrice: parseFloat(item.entry_price),
          exitPrice: parseFloat(item.exit_price),
          amount: parseFloat(item.amount),
          leverage: item.leverage,
          margin: parseFloat(item.margin),
          pnl: parseFloat(item.pnl),
          roi: parseFloat(item.roi),
          openTime: item.open_time,
          closeTime: item.close_time,
          durationSeconds: item.duration_seconds
        })));
      }
    } catch (err: any) {
      console.error('Error loading position history:', err);
      setError(err.message || 'Failed to load position history');
    } finally {
      setLoading(false);
    }
  }, []);

  // Cancel all open orders
  const cancelAllOpenOrders = useCallback(async (): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      // Get all open orders for the user
      const { data: openOrders, error: ordersError } = await supabase
        .from('futures_orders')
        .select('*')
        .eq('status', 'open')
        .eq('user_id', user.id);
        
      if (ordersError) {
        throw ordersError;
      }
      
      if (!openOrders || openOrders.length === 0) {
        return true; // No orders to cancel
      }
      
      // For each order, calculate the margin and refund it to the user
      let totalRefund = 0;
      
      for (const order of openOrders) {
        // Calculate the margin that was reserved for this order
        const price = order.price || 0;
        const notionalValue = order.amount * price;
        const margin = notionalValue / order.leverage;
        
        totalRefund += margin;
      }
      
      // Update all orders to cancelled status
      const { error: updateError } = await supabase
        .from('futures_orders')
        .update({ status: 'cancelled' })
        .eq('status', 'open')
        .eq('user_id', user.id);
        
      if (updateError) {
        throw updateError;
      }
      
      // Refund the total margin to the user's balance
      if (totalRefund > 0) {
        await updateBalances({
          usdt_balance: balances.usdt_balance + totalRefund
        });
      }
      
      // Refresh open orders
      await fetchOpenOrders();
      
      return true;
    } catch (err: any) {
      console.error('Error cancelling open orders:', err);
      setError(err.message || 'Failed to cancel open orders');
      return false;
    } finally {
      setLoading(false);
    }
  }, [balances, updateBalances, fetchOpenOrders]);

  // Cancel a specific order
  const cancelOrder = useCallback(async (orderId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    
    try {
      // Get the order details
      const { data: order, error: orderError } = await supabase
        .from('futures_orders')
        .select('*')
        .eq('id', orderId)
        .single();
        
      if (orderError) {
        throw orderError;
      }
      
      // Calculate the margin to refund
      const price = order.price || 0;
      const notionalValue = order.amount * price;
      const margin = notionalValue / order.leverage;
      
      // Update order status to cancelled
      const { error: updateError } = await supabase
        .from('futures_orders')
        .update({ status: 'cancelled' })
        .eq('id', orderId);
        
      if (updateError) {
        throw updateError;
      }
      
      // Refund margin to user's balance
      await updateBalances({
        usdt_balance: balances.usdt_balance + margin
      });
      
      // Refresh open orders
      await fetchOpenOrders();
      
      return true;
    } catch (err: any) {
      console.error('Error cancelling order:', err);
      setError(err.message || 'Failed to cancel order');
      return false;
    } finally {
      setLoading(false);
    }
  }, [balances, updateBalances, fetchOpenOrders]);

  return {
    openPosition,
    closePosition,
    updateEntryPrice,
    updateStopLossTakeProfit,
    loadPositionHistory,
    fetchActivePositions,
    fetchOpenOrders,
    cancelAllOpenOrders,
    cancelOrder,
    positionHistory,
    activePositions,
    openOrders,
    loading,
    error,
    calculateLiquidationPrice
  };
};